import { Paragraph, TextRun, Table, PageBreak, ImageRun } from 'docx';
import { supabase } from '@/integrations/supabase/client';
import { WorkPreparation } from '@/types/arbetsberedning';
import { evaluateCompleteness } from '@/lib/arbetsberedning/completeness';
import { downloadWordDocument } from "@/lib/documentGenerator/exportHistory";
import {
  REVISION_HEADERS,
  currentPhaseName,
  formatRevisionDate,
} from "@/lib/documentGenerator/revisionColumns";
import { createStampForCurrentUser } from "@/lib/documentGenerator/watermark";
import {
  body as themeBody,
  buildDocument,
  bullet as themeBullet,
  factTable,
  h1 as themeH1,
  h2 as themeH2,
  spacer as themeSpacer,
  table as themeTable,
  tableOfContents as themeToc,
} from '@/lib/documentGenerator/docTheme';
import { documentFooter } from "@/lib/documentGenerator/documentFooter";

export interface ArbetsberedningExportOptions {
  includeProjectInfo?: boolean;
  includeRisks?: boolean;
  includeChecklist?: boolean;
  includeSignatures?: boolean;
  includeMaterials?: boolean;
}

const s = (v: unknown) => (v === undefined || v === null ? '' : String(v));

const gridTable = (columnWidths: number[], headers: string[], rows: (string | null | undefined)[][]) =>
  themeTable(headers, rows, { columnWidths });

/** Två-kolumns nyckel/värde-tabell */
const infoTable = (rows: [string, string][]) => factTable(rows);

const h1 = (text: string) => themeH1(text);
const h2 = (text: string) => themeH2(text);
const spacer = () => themeSpacer(120);

const textBlock = (value?: string | null) =>
  s(value)
    .split('\n')
    .filter((line) => line.trim().length > 0)
    .map((line) => themeBody(line.trim()));

const bullets = (items: string[]) => items.map((t) => themeBullet(s(t)));

const REVIEW_STATUS_LABELS: Record<string, string> = {
  utkast: 'Utkast',
  inskickad: 'Inskickad för granskning',
  komplettering: 'Komplettering begärd',
  godkand: 'Godkänd av Bas-U',
};

const AVSNITT_LABELS: Record<string, string> = {
  lyft: 'Lyft och lyftanordningar',
  elsakerhet: 'Elsäkerhet',
  exponering: 'Exponering (damm, buller, kemikalier)',
  samordning: 'Samordning med andra aktörer',
  hetaArbeten: 'Heta arbeten',
  trafik: 'Trafik och fordonsrörelser',
};

const MATERIAL_STATUS_LABELS: Record<string, string> = {
  ej_kontrollerat: 'Ej kontrollerat',
  kontrollerat_utan_fynd: 'Kontrollerat – inget påträffat',
  misstanke: 'Misstanke finns',
  patraffat: 'Asbest/farligt material påträffat',
  inte_relevant: 'Inte relevant',
};

const riskBlock = (risk: { risk?: string; organisatoriskAtgard?: string; tekniskAtgard?: string; ansvarig?: string; afsReferens?: string }) => [
  factTable([
    ['Risk', s(risk.risk)],
    ['Organisatorisk åtgärd', s(risk.organisatoriskAtgard)],
    ['Teknisk åtgärd', s(risk.tekniskAtgard)],
    ['Ansvarig', `${s(risk.ansvarig)}${risk.afsReferens ? `   |   AFS: ${risk.afsReferens}` : ''}`],
  ]),
  themeSpacer(100),
];


export const generateArbetsberedningWordDocument = async (
  workPreparation: WorkPreparation,
  options: ArbetsberedningExportOptions = {}
): Promise<void> => {
  const stamp = await createStampForCurrentUser({
    projectName: (workPreparation as { project_name?: string }).project_name || "",
    docNumber: "24",
    documentName: workPreparation.name || "Arbetsberedning",
  });
  const {
    includeProjectInfo = true,
    includeRisks = true,
    includeChecklist = true,
    includeSignatures = true,
    includeMaterials = true,
  } = options;

  const fields = workPreparation.fields;
  const moment = [...(fields.moment ?? [])].sort((a, b) => a.ordning - b.ordning);
  const kontrollpunkter = fields.kontrollpunkter ?? [];
  const avsnittEntries = Object.entries(fields.sarskildaAvsnitt ?? {}).filter(
    ([, v]) => !!s(v).trim()
  );
  const checklista = fields.förutsättningarChecklista ?? [];
  const antalRisker = moment.reduce((sum, m) => sum + (m.risker?.length ?? 0), 0);

  let completeness: { percent: number; missingBlocking: { label: string }[] } | null = null;
  try {
    const res = evaluateCompleteness(workPreparation);
    completeness = { percent: res.percent, missingBlocking: res.missingBlocking };
  } catch {
    completeness = null;
  }

  const reviewLabel =
    REVIEW_STATUS_LABELS[workPreparation.reviewStatus ?? 'utkast'] ?? 'Utkast';

  /* ---------- Sida 1: sammanställning och checklista ---------- */
  const summaryPage: (Paragraph | Table)[] = [
    h1('1. Sammanställning'),

    infoTable([
      ['Projektnr/arbetsplats', [s(fields.projektnr), s(fields.arbetsplats)].filter(Boolean).join(' – ')],
      ['Datum', s(fields.datum)],
      ['Gjord av', s(fields.gjordAv)],
      ['Status', reviewLabel],
      ['Läge', workPreparation.mode === 'kopplad' ? 'Kopplad till projektets arbetsmiljöplan' : 'Fristående arbetsberedning'],
      ['Tidsplan', [s(fields.startdatum), s(fields.slutdatum)].filter(Boolean).join(' – ') || '–'],
      [
        'Omfattning',
        `${moment.length} moment · ${antalRisker} identifierade risker · ${kontrollpunkter.length} kontrollpunkter`,
      ],
      [
        'Särskilda avsnitt',
        avsnittEntries.length
          ? avsnittEntries.map(([k]) => AVSNITT_LABELS[k] || k).join(', ')
          : 'Inga särskilda avsnitt aktuella',
      ],
      ...(completeness
        ? ([
            [
              'Fullständighet',
              completeness.missingBlocking.length === 0
                ? `${completeness.percent} % – alla obligatoriska delar på plats`
                : `${completeness.percent} % – saknas: ${completeness.missingBlocking
                    .map((m) => m.label)
                    .join(', ')}`,
            ],
          ] as [string, string][])
        : []),
      ...(workPreparation.reviewStatus === 'godkand'
        ? ([
            [
              'Godkännande',
              `Godkänd av Bas-U${workPreparation.reviewedBy ? ` (${workPreparation.reviewedBy})` : ''}${
                workPreparation.reviewedAt
                  ? ` ${new Date(workPreparation.reviewedAt).toLocaleDateString('sv-SE')}`
                  : ''
              }`,
            ],
          ] as [string, string][])
        : []),
    ]),
    spacer(),
  ];

  if (includeChecklist && checklista.length > 0) {
    summaryPage.push(
      h2('Checklista – förutsättningar och kontroller före start'),
      gridTable(
        [4560, 2400, 1200, 1200],
        ['Kontrollpunkt', 'Bevis/dokumentation', 'Prioritet', 'Kontrollerad'],
        checklista.map((item) => [
          item.item || 'Ej specificerad',
          item.evidence || '-',
          item.priority ? item.priority.charAt(0).toUpperCase() + item.priority.slice(1) : '-',
          '☐',
        ])
      ),
      spacer()
    );
  }

  if (kontrollpunkter.length > 0) {
    summaryPage.push(
      h2('Kontroll- och uppföljningspunkter'),
      gridTable(
        [3960, 1800, 1800, 1800],
        ['Kontrollpunkt', 'När', 'Ansvarig', 'Metod'],
        kontrollpunkter.map((k) => [k.punkt || '', k.nar || '', k.ansvarig || '', k.metod || ''])
      ),
      spacer()
    );
  }

  /* ---------- Sida 2+: fullständigt innehåll ---------- */
  const body: (Paragraph | Table)[] = [];

  const kompetenser = fields.kompetensVerifieringar ?? [];
  if (kompetenser.length > 0) {
    body.push(h1('Kompetenser och behörigheter'));
    body.push(
      gridTable(
        [1560, 1860, 1860, 1140, 1440, 1500],
        ['Person/roll', 'Krav', 'Verifiering', 'Giltig till', 'Verifierad av/datum', 'Status'],
        kompetenser.map((k) => {
          const expired = !!k.giltigTill && new Date(k.giltigTill) < new Date(new Date().toDateString());
          const status =
            k.status === 'ej_aktuell'
              ? `Ej aktuell${k.motiveringEjAktuell ? ` – ${k.motiveringEjAktuell}` : ''}`
              : expired
              ? 'Utgånget intyg'
              : k.status === 'verifierad'
              ? 'Verifierad'
              : 'EJ VERIFIERAD';
          const bevis = (k.bevis ?? []).length ? ` · Intyg bifogat (${(k.bevis ?? []).length})` : ' · Intyg saknas';
          return [
            s(k.personEllerRoll),
            s(k.krav),
            s(k.verifiering),
            s(k.giltigTill) || '–',
            [s(k.verifieradAv), s(k.verifieradDatum)].filter(Boolean).join(' ') || '–',
            status + bevis,
          ];
        })
      ),
      spacer()
    );
  }


  const materialKontroll = fields.befintligtMaterialKontroll;
  if (materialKontroll) {
    body.push(h1('Kontroll av asbest och befintliga material'));
    body.push(infoTable([
      ['Status', MATERIAL_STATUS_LABELS[materialKontroll.status] || materialKontroll.status],
      ['Underlag/provtagning', s(materialKontroll.underlag) || '–'],
      ['Resultat', s(materialKontroll.resultat) || '–'],
      ['Ansvarig och datum', [s(materialKontroll.ansvarig), s(materialKontroll.datum)].filter(Boolean).join(' – ') || '–'],
      ['Åtgärd/motivering', s(materialKontroll.atgarder || materialKontroll.motiveringInteRelevant) || '–'],
    ]));
    body.push(spacer());
  }

  body.push(h1('Beskrivning av arbetet'));
  body.push(...(textBlock(fields.beskrivArbetet).length ? textBlock(fields.beskrivArbetet) : [new Paragraph({ text: 'Ingen beskrivning angiven.' })]));
  if (fields.vilkaUtför?.length) {
    body.push(h2('Vilka ska utföra arbetet'));
    body.push(...bullets(fields.vilkaUtför));
  }
  if (fields.startdatum || fields.slutdatum || fields.timmar) {
    body.push(h2('Tidsplanering'));
    body.push(
      infoTable([
        ['Startdatum', s(fields.startdatum) || '–'],
        ['Slutdatum', s(fields.slutdatum) || '–'],
        ['Beräknade timmar', fields.timmar ? String(fields.timmar) : '–'],
      ])
    );
    body.push(spacer());
  }

  if (moment.length > 0) {
    body.push(h1('Arbetsmoment, risker och åtgärder'));
    moment.forEach((m) => {
      body.push(h2(`${m.ordning}. ${m.titel || 'Moment'}`));
      const meta: [string, string][] = [
        ['Syfte', s(m.syfte) || '–'],
        ['Ansvarig', s(m.ansvarigRoll) || '–'],
        ['Utrustning', s(m.utrustning) || '–'],
      ];
      body.push(infoTable(meta));
      body.push(spacer());
      if ((m.risker ?? []).length > 0) {
        m.risker.forEach((risk) => body.push(...riskBlock(risk)));
      }
    });
  }

  if (avsnittEntries.length > 0) {
    body.push(h1('Särskilda avsnitt'));
    avsnittEntries.forEach(([key, value]) => {
      body.push(h2(AVSNITT_LABELS[key] || key));
      body.push(...textBlock(value as string));
    });
    body.push(spacer());
  }

  if (fields.avvikandeMetod?.finns) {
    body.push(h1('Avvikande arbetsmetod'));
    body.push(
      infoTable([
        ['Beskrivning', s(fields.avvikandeMetod.beskrivning) || '–'],
        ['Motivering', s(fields.avvikandeMetod.motivering) || '–'],
        ['Extra åtgärder', s(fields.avvikandeMetod.atgarder) || '–'],
      ])
    );
    body.push(spacer());
  }

  if (includeRisks && fields.kändaRisker?.length) {
    body.push(h1('Kända risker från underlag'));
    body.push(
      gridTable(
        [2400, 3360, 1800, 1800],
        ['Risk', 'Beskrivning', 'Organisatorisk åtgärd', 'Teknisk åtgärd'],
        fields.kändaRisker.map((risk) => [
          risk.title || 'Ej specificerad',
          risk.description || '-',
          risk.organisatoriskAtgard || '-',
          risk.tekniskAtgard || '-',
        ])
      )
    );
    body.push(spacer());
  }

  const freeTextSections: [string, string | undefined][] = [
    ['Riskåtgärder', fields.riskåtgärder],
    ['Ritningar och beskrivning', fields.ritningarBeskrivning],
    ['Andra förutsättningar', fields.andraFörutsättningar],
    ['Koordinering', fields.koordinering],
    ['Kemikalier', fields.kemikalier],
    ['Fortsättning risker och åtgärder', fields.fortsättningRiskerÅtgärder],
    ['Uppföljning', fields.uppföljning],
  ];
  freeTextSections.forEach(([title, value]) => {
    if (s(value).trim()) {
      body.push(h1(title));
      body.push(...textBlock(value));
      body.push(spacer());
    }
  });

  if (fields.arbetsmoment?.length) {
    body.push(h1('Arbetsmoment (fritext)'));
    body.push(...bullets(fields.arbetsmoment));
    body.push(spacer());
  }

  if (fields.verktygMaskiner?.length) {
    body.push(h1('Verktyg och maskiner'));
    body.push(...bullets(fields.verktygMaskiner));
    body.push(spacer());
  }

  if (includeMaterials && fields.hanteringMaterial) {
    const mat = [
      fields.hanteringMaterial.trä ? 'Trä' : null,
      fields.hanteringMaterial.plast ? 'Plast' : null,
      fields.hanteringMaterial.metall ? 'Metall' : null,
      fields.hanteringMaterial.farligtAvfall ? 'Farligt avfall' : null,
      fields.hanteringMaterial.fritext || null,
    ].filter(Boolean) as string[];
    if (mat.length) {
      body.push(h1('Hantering av material'));
      body.push(...bullets(mat));
      body.push(spacer());
    }
  }

  if (includeProjectInfo && (fields.projektnr || fields.arbetsplats || fields.kontaktinformation)) {
    body.push(h1('Projektinformation'));
    body.push(
      infoTable([
        ['Projektnr', s(fields.projektnr) || '–'],
        ['Arbetsplats', s(fields.arbetsplats) || '–'],
        ['Datum', s(fields.datum) || '–'],
        ['Gjord av', s(fields.gjordAv) || '–'],
        ['Kontaktinformation', s(fields.kontaktinformation) || '–'],
      ])
    );
    body.push(spacer());
  }

  if (includeSignatures) {
    body.push(h1('Signaturer och deltagare'));
    const sigs = fields.signaturer ?? [];
    body.push(
      gridTable(
        [2800, 2000, 1760, 2800],
        ['Namn', 'Roll', 'Datum', 'Underskrift'],
        (sigs.length ? sigs : [{ namn: '', roll: '', datum: '' }, { namn: '', roll: '', datum: '' }]).map(
          (sig) => [sig.namn || '', sig.roll || '', sig.datum || '', '']
        )
      )
    );
    body.push(spacer());
  }

  /* ---------- Revisionshistorik ---------- */
  {
    const revRows: string[][] = [];
    if (workPreparation.submittedAt) {
      revRows.push([
        formatRevisionDate(workPreparation.submittedAt),
        s(fields.gjordAv) || '–',
        currentPhaseName(),
        'Inlämnad för granskning',
      ]);
    }
    if (workPreparation.reviewedAt) {
      revRows.push([
        formatRevisionDate(workPreparation.reviewedAt),
        s(workPreparation.reviewedBy) || '–',
        currentPhaseName(),
        [reviewLabel, s(workPreparation.reviewComment)].filter(Boolean).join(' – '),
      ]);
    }
    if (revRows.length === 0) {
      revRows.push([
        formatRevisionDate(s(fields.datum) || new Date().toISOString()),
        s(fields.gjordAv) || '–',
        currentPhaseName(),
        reviewLabel,
      ]);
    }
    body.push(h1('Revisionshistorik'));
    body.push(
      gridTable([1800, 2400, 2000, 3160], [...REVISION_HEADERS], revRows)
    );
    body.push(spacer());
  }

  /* ---------- Bilaga: uppladdade intyg ---------- */
  const bevisLista = kompetenser.flatMap((k) => (k.bevis ?? []).map((b) => ({ ...b, krav: k.krav, person: k.personEllerRoll })));
  if (bevisLista.length > 0) {
    body.push(new Paragraph({ children: [new PageBreak()] }));
    body.push(h1('Bilaga – verifierade intyg'));
    for (const b of bevisLista) {
      body.push(
        new Paragraph({
          spacing: { before: 160, after: 80 },
          children: [new TextRun({ text: `${b.krav}${b.person ? ` – ${b.person}` : ''} (${b.namn})`, bold: true, size: 22 })],
        })
      );
      if (b.typ?.startsWith('image/')) {
        try {
          const { data } = await supabase.storage.from('kompetensbevis').createSignedUrl(b.path, 300);
          if (data?.signedUrl) {
            const res = await fetch(data.signedUrl);
            const buf = await res.arrayBuffer();
            const ext = (b.typ.split('/')[1] || 'png').replace('jpeg', 'jpg');
            body.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    type: (['png', 'jpg', 'gif', 'bmp'].includes(ext) ? ext : 'png') as 'png',
                    data: buf,
                    transformation: { width: 420, height: 300 },
                    altText: { title: b.krav, description: b.namn, name: b.namn },
                  }),
                ],
              })
            );
          }
        } catch {
          body.push(new Paragraph({ text: 'Bilden kunde inte hämtas – se digitalt intyg i systemet.' }));
        }
      } else {
        body.push(new Paragraph({ text: 'PDF-intyg finns uppladdat i systemet.' }));
      }
    }
    body.push(spacer());
  }
  const footer = await documentFooter({ docNumber: "24", documentType: "arbetsberedning", includeRevisions: false });

  const doc = buildDocument({
    meta: {
      version: footer.version,
      review: footer.review,
      docNumber: '26',
      documentName: 'Arbetsberedning',
      projectName:
        (workPreparation as { project_name?: string }).project_name || s(fields.arbetsplats),
      status: reviewLabel,
      createdBy: s(fields.gjordAv),
      date: s(fields.datum) || null,
      extra: [
        ['Arbetsberedning', workPreparation.name || ''],
        ['Projektnummer', s(fields.projektnr)],
      ],
    },
    stamp,
    children: [...summaryPage, ...themeToc(), ...body],
  });


  await downloadWordDocument(doc, `Arbetsberedning_${workPreparation.name || 'Dokument'}.docx`, {
    documentType: "arbetsberedning",
    docNumber: "24",
    documentId: (workPreparation as { id?: string }).id ?? null,
    documentName: workPreparation.name || "Arbetsberedning",
    snapshot: workPreparation,
    stamp,
  });
};
